import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { UtmPreservationService } from './utm-preservation.service';
import { ChannelIndicationRequest } from './indication.service';

const EMPLOYEE_FIELDS = [
  'referrerName',
  'referrerEmail',
  'referrerPhone',
  'referrerArea',
  'referrerCity',
  'referrerState',
] as const;

const COMPANY_REQUIRED_FIELDS = [
  'contactName',
  'companyName',
  'companyCNPJ',
  'companyEmail',
  'companyPhone',
  'bestContactTime',
  'bestContactMethod',
  'companyAwareOfReferral',
] as const;

/**
 * Channel/UTM indication helpers — keeps IndicationComponent thinner.
 */
@Injectable({
  providedIn: 'root',
})
export class IndicationChannelHelper {
  private utmPreservationService = inject(UtmPreservationService);

  isChannelMode(): boolean {
    if (this.utmPreservationService.isUtmEnabled()) {
      return true;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      return Array.from(params.keys()).some(k => k.toLowerCase().startsWith('utm_'));
    } catch {
      return false;
    }
  }

  getUtmSource(): string {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('utm_source');
      if (fromUrl?.trim()) {
        return fromUrl.trim();
      }
    } catch {
      // ignore
    }
    return (this.utmPreservationService.preservedUtmParams?.['utm_source'] || '').trim();
  }

  prepareFormForChannelMode(form: FormGroup): void {
    EMPLOYEE_FIELDS.forEach(field => {
      const control = form.get(field);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  getRequiredFields(isChannelMode: boolean): string[] {
    if (isChannelMode) {
      return [...COMPANY_REQUIRED_FIELDS];
    }
    return [...EMPLOYEE_FIELDS, ...COMPANY_REQUIRED_FIELDS];
  }

  buildRequest(formValue: Record<string, any>): ChannelIndicationRequest {
    return {
      indication: {
        name: formValue['contactName'],
        companyName: formValue['companyName'],
        document: this.digitsOnly(formValue['companyCNPJ']),
        phone: this.digitsOnly(formValue['companyPhone']),
        email: formValue['companyEmail'],
      },
      complementaryData: {
        bestContactTime: formValue['bestContactTime'] || '',
        contactMethod: formValue['bestContactMethod'] || '',
        additionalInfo: formValue['additionalInformation'] || '',
        companyAware: formValue['companyAwareOfReferral'] === 'sim',
      },
      utm: this.getUtmSource(),
    };
  }

  private digitsOnly(value: string): string {
    return value ? value.replace(/\D/g, '') : '';
  }
}
